const MAX_DISPLAYED_IMAGES = 12;
const IMAGE_WIDTHS = [320, 480, 640, 800, 1000, 1200, 1600, 2000];

/**
 * Randomize an array in place using a Fisher-Yates shuffle.
 *
 * @template T
 * @param {T[]} items
 * @returns {T[]}
 */
function shuffle(items) {
  for (let index = items.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [items[index], items[randomIndex]] = [items[randomIndex], items[index]];
  }

  return items;
}

/**
 * Return a Shopify CDN image URL at the requested width.
 *
 * @param {string} source
 * @param {number} width
 * @returns {string}
 */
function imageUrlAtWidth(source, width) {
  const url = new URL(source, window.location.origin);
  url.searchParams.set('width', String(width));
  return url.toString();
}

/**
 * @typedef {Object} SeenOnYouCandidate
 * @property {string|number} id
 * @property {string|number} siblingId
 * @property {string} [src]
 * @property {number} [width]
 * @property {number} [height]
 * @property {string} [alt]
 * @property {string} [focalPoint]
 */

/**
 * @typedef {SeenOnYouCandidate & { siblingIds: string[] }} NormalizedSeenOnYouCandidate
 */

class SeenOnYouCarousel extends HTMLElement {
  static get observedAttributes() {
    return ['data-product-id'];
  }

  connectedCallback() {
    this.initialize();
  }

  /**
   * Reinitialize after Horizon morphs a connected product into the existing PDP.
   * The attribute is updated before the new child markup is morphed, so wait until
   * the current task finishes before reading the replacement candidate script.
   *
   * @param {string} name
   * @param {string|null} oldValue
   * @param {string|null} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'data-product-id' || oldValue === null || oldValue === newValue || !this.isConnected) return;

    queueMicrotask(() => this.initialize());
  }

  initialize() {
    if (this.initializing) return;

    const candidateData = this.querySelector('[data-seen-on-you-candidates]');
    if (!(candidateData instanceof HTMLScriptElement)) return;

    this.initializing = true;

    try {
      /** @type {SeenOnYouCandidate[]} */
      const candidates = JSON.parse(candidateData.textContent || '[]');
      candidateData.remove();

      const slidesContainer = this.querySelector('slideshow-slides');
      if (!(slidesContainer instanceof HTMLElement)) return;

      const configuredMaximum = Number.parseInt(this.dataset.maxEntries || '', 10);
      const maximum = Math.min(
        Number.isFinite(configuredMaximum) ? Math.max(configuredMaximum, 1) : MAX_DISPLAYED_IMAGES,
        MAX_DISPLAYED_IMAGES
      );
      const existingSlides = Array.from(slidesContainer.querySelectorAll(':scope > slideshow-slide'));
      const remainingSlots = maximum - existingSlides.length;

      if (remainingSlots > 0 && Array.isArray(candidates)) {
        const existingIds = new Set(
          Array.from(this.querySelectorAll('[data-seen-on-you-entry-id]'), (card) =>
            card.getAttribute('data-seen-on-you-entry-id')
          ).filter(Boolean)
        );
        const uniqueCandidates = this.normalizeCandidates(candidates, existingIds);
        const selectedCandidates = this.selectCandidates(uniqueCandidates, remainingSlots);

        for (const [offset, candidate] of selectedCandidates.entries()) {
          slidesContainer.append(this.createSlide(candidate, existingSlides.length + offset));
        }
      }

      this.synchronizeSlideshow();
      if (slidesContainer.querySelector(':scope > slideshow-slide')) this.hidden = false;
    } catch (error) {
      console.error('Unable to initialize Seen on you carousel.', error);
    } finally {
      this.initializing = false;
    }
  }

  /**
   * Collapse every metaobject ID to one candidate ticket while retaining all eligible siblings.
   * Do not use sibling associations during normalization so repeated references cannot affect
   * the candidate's probability of being sampled.
   *
   * @param {SeenOnYouCandidate[]} candidates
   * @param {Set<string>} existingIds
   * @returns {NormalizedSeenOnYouCandidate[]}
   */
  normalizeCandidates(candidates, existingIds) {
    /** @type {Map<string, { candidate?: SeenOnYouCandidate, siblingIds: Set<string> }>} */
    const uniqueCandidates = new Map();

    for (const candidate of candidates) {
      const id = String(candidate?.id || '');
      const siblingId = String(candidate?.siblingId || '');

      if (!id || !siblingId || existingIds.has(id)) continue;

      const uniqueCandidate = uniqueCandidates.get(id) || { siblingIds: new Set() };
      if (candidate?.src && !uniqueCandidate.candidate) uniqueCandidate.candidate = candidate;
      uniqueCandidate.siblingIds.add(siblingId);
      uniqueCandidates.set(id, uniqueCandidate);
    }

    return Array.from(uniqueCandidates.values(), ({ candidate, siblingIds }) =>
      candidate ? { ...candidate, siblingIds: Array.from(siblingIds) } : null
    ).filter((candidate) => candidate !== null);
  }

  /**
   * Sample unique image tickets without considering their sibling-reference counts, then order
   * the sampled images into maximum-coverage sibling rounds.
   *
   * @param {NormalizedSeenOnYouCandidate[]} candidates
   * @param {number} limit
   * @returns {SeenOnYouCandidate[]}
   */
  selectCandidates(candidates, limit) {
    /** @type {SeenOnYouCandidate[]} */
    const selected = [];
    let remaining = shuffle([...candidates]).slice(0, limit);

    while (remaining.length > 0) {
      const round = this.buildCoverageRound(remaining);
      if (round.length === 0) break;

      selected.push(...round);
      const roundIds = new Set(round.map((candidate) => String(candidate.id || '')));
      remaining = remaining.filter((candidate) => !roundIds.has(String(candidate.id || '')));
    }

    return selected;
  }

  /**
   * Build one maximum-cardinality matching between candidates and eligible siblings. Each
   * returned candidate has a distinct sibling owner, maximizing color coverage for the round.
   * Candidate and sibling traversal are shuffled so equivalent maximum matchings remain random.
   *
   * @param {NormalizedSeenOnYouCandidate[]} candidates
   * @returns {SeenOnYouCandidate[]}
   */
  buildCoverageRound(candidates) {
    /** @type {Map<string, NormalizedSeenOnYouCandidate>} */
    const siblingAssignments = new Map();

    /**
     * @param {NormalizedSeenOnYouCandidate} candidate
     * @param {Set<string>} visitedSiblingIds
     * @returns {boolean}
     */
    const assignCandidate = (candidate, visitedSiblingIds) => {
      for (const siblingId of shuffle([...candidate.siblingIds])) {
        if (visitedSiblingIds.has(siblingId)) continue;
        visitedSiblingIds.add(siblingId);

        const assignedCandidate = siblingAssignments.get(siblingId);
        if (!assignedCandidate || assignCandidate(assignedCandidate, visitedSiblingIds)) {
          siblingAssignments.set(siblingId, candidate);
          return true;
        }
      }

      return false;
    };

    for (const candidate of shuffle([...candidates])) assignCandidate(candidate, new Set());

    return shuffle(
      Array.from(siblingAssignments, ([siblingId, candidate]) => ({ ...candidate, siblingId }))
    );
  }

  /**
   * @param {SeenOnYouCandidate} candidate
   * @param {number} index
   * @returns {HTMLElement}
   */
  createSlide(candidate, index) {
    const slide = document.createElement('slideshow-slide');
    slide.className = 'resource-list__slide';
    slide.setAttribute('ref', 'slides[]');
    slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
    slide.style.setProperty('--slideshow-timeline', `--slide-${index}`);
    slide.style.setProperty('--product-media-fit', 'cover');
    slide.style.setProperty('--grid-template-rows', '50dvh 1fr');
    slide.style.setProperty('--grid-template-rows-desktop', '70dvh 1fr');

    const card = document.createElement('div');
    card.className = 'resource-list__item seen-on-you__card';
    card.dataset.seenOnYouEntryId = String(candidate.id);
    card.style.setProperty('--seen-on-you-image-ratio', this.dataset.imageRatio || '4 / 5');

    const image = document.createElement('img');
    const originalWidth = Number(candidate.width) || 2000;
    const originalHeight = Number(candidate.height) || 0;
    const maximumSourceWidth = Math.min(originalWidth, 2000);
    const widths = IMAGE_WIDTHS.filter((width) => width <= maximumSourceWidth);

    if (!widths.includes(maximumSourceWidth)) widths.push(maximumSourceWidth);

    image.className = 'seen-on-you__image';
    image.alt = candidate.alt || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    image.sizes = this.dataset.imageSizes || '60vw';
    image.src = imageUrlAtWidth(candidate.src, maximumSourceWidth);
    image.srcset = widths.map((width) => `${imageUrlAtWidth(candidate.src, width)} ${width}w`).join(', ');
    image.width = originalWidth;
    if (originalHeight > 0) image.height = originalHeight;
    if (candidate.focalPoint) image.style.objectPosition = candidate.focalPoint;

    card.append(image);
    slide.append(card);
    return slide;
  }

  synchronizeSlideshow() {
    const slideshow = this.querySelector('slideshow-component');
    if (!(slideshow instanceof HTMLElement)) return;

    const slideCount = slideshow.querySelectorAll('slideshow-slides > slideshow-slide').length;
    slideshow.setAttribute('disabled', String(slideCount <= 1));

    if (!customElements.get('slideshow-component')) return;

    const parent = slideshow.parentNode;
    if (!parent) return;

    const nextSibling = slideshow.nextSibling;
    slideshow.remove();
    parent.insertBefore(slideshow, nextSibling);
  }
}

if (!customElements.get('seen-on-you-carousel')) {
  customElements.define('seen-on-you-carousel', SeenOnYouCarousel);
}

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;

  const carousels = event.target.matches('seen-on-you-carousel')
    ? [event.target]
    : Array.from(event.target.querySelectorAll('seen-on-you-carousel'));

  for (const carousel of carousels) {
    if (carousel instanceof SeenOnYouCarousel) carousel.initialize();
  }
});
